import { handleResolveFlaggedStudy } from '@/entities/study';
import { showMessageError, dateToDuration, dateToTimeSince } from '@/shared/lib';
import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import type { MarkAddressedStudyProps } from './markAddressedStudy';

/**
 * Drives the mark-as-addressed popup of a flagged study row in the studies list.
 * Holds the popup open and loading flags, memoizes the time since the issue was last addressed,
 * resolves the flagged study with optimistic list updates, and closes the popup once the study
 * is resolved elsewhere or the control is hidden.
 *
 * @param props - Row props carrying lastAddressedTime, studyId, onUpdateMarkAddressed, resolvedAt
 * and isHideMarkAddressedStudy.
 * @returns The open flag and its setter, the loading flag, the formatted time and the
 * mark-addressed handler.
 */
export const useMarkAddressedStudy = ({
  lastAddressedTime,
  studyId,
  onUpdateMarkAddressed,
  resolvedAt,
  isHideMarkAddressedStudy,
}: MarkAddressedStudyProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  /**
   * Holds the human-readable elapsed time since the study's issue was last addressed. Memoised on lastAddressedTime by converting it to a duration, turning that into a time-since phrase and lower-casing the result.
   *
   * @returns Lower-cased time-since text for the last addressed time.
   */
  const formatTime = useMemo(
    () => _.lowerCase(dateToTimeSince(dateToDuration(lastAddressedTime))),
    [lastAddressedTime],
  );
  /**
   * Marks a flagged study as addressed from the studies list, optionally ignoring further flag checks. Optimistically notifies the list through onUpdateMarkAddressed, awaits handleResolveFlaggedStudy for the study, then closes the popup and re-notifies; on failure it logs, shows a failure message and asks the list to roll back, and always clears the loading flag.
   *
   * @param isIgnoreChecked - Whether the study's future flags should be ignored as well.
   * @returns A promise resolving once the study has been resolved or the optimistic update rolled back.
   */
  const onClickMarkAddressed = async (isIgnoreChecked: boolean) => {
    try {
      setLoading(true);
      if (onUpdateMarkAddressed) {
        onUpdateMarkAddressed(false, studyId);
      }
      await handleResolveFlaggedStudy(studyId, true, isIgnoreChecked);
      setIsOpen(false);
      if (onUpdateMarkAddressed) {
        onUpdateMarkAddressed();
      }
    } catch (error) {
      console.log('Error marking study as addressed: ', error);
      showMessageError({ content: 'Failed to mark issue as addressed. Please try again.' });
      if (onUpdateMarkAddressed) {
        onUpdateMarkAddressed(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((resolvedAt || isHideMarkAddressedStudy) && isOpen) {
      setIsOpen(false);
    }
  }, [resolvedAt, isHideMarkAddressedStudy]);

  return {
    isOpen,
    setIsOpen,
    loading,
    formatTime,
    onClickMarkAddressed,
  };
};
